import React, { useRef, useEffect, useState, useCallback } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Subjects that use mathematical symbols
export const MATH_SUBJECTS = [
  'Mathematics',
  'Additional Mathematics',
  'Additional Math',
  'Physics',
  'Chemistry',
  'Biology',
  'Principles of Accounts',
  'POA',
  'Economics',
  'Integrated Science',
];

// Check if a subject uses math symbols
export const subjectUsesMath = subjectName => {
  if (!subjectName) return false;
  const normalized = subjectName.toLowerCase();
  return MATH_SUBJECTS.some(s => normalized.includes(s.toLowerCase()));
};

// Symbol categories like Google Docs
const SYMBOL_CATEGORIES = [
  {
    name: 'Common',
    symbols: [
      { symbol: '±', latex: '\\pm', name: 'Plus-minus' },
      { symbol: '×', latex: '\\times', name: 'Multiply' },
      { symbol: '÷', latex: '\\div', name: 'Divide' },
      { symbol: '≠', latex: '\\neq', name: 'Not equal' },
      { symbol: '≈', latex: '\\approx', name: 'Approximately' },
      { symbol: '≤', latex: '\\leq', name: 'Less than or equal' },
      { symbol: '≥', latex: '\\geq', name: 'Greater than or equal' },
      { symbol: '<', latex: '<', name: 'Less than' },
      { symbol: '>', latex: '>', name: 'Greater than' },
      { symbol: '∞', latex: '\\infty', name: 'Infinity' },
      { symbol: '°', latex: '^{\\circ}', name: 'Degree' },
      { symbol: '%', latex: '\\%', name: 'Percent' },
      { symbol: '·', latex: '\\cdot', name: 'Dot multiply' },
    ],
  },
  {
    name: 'Greek',
    symbols: [
      { symbol: 'α', latex: '\\alpha', name: 'Alpha' },
      { symbol: 'β', latex: '\\beta', name: 'Beta' },
      { symbol: 'γ', latex: '\\gamma', name: 'Gamma' },
      { symbol: 'δ', latex: '\\delta', name: 'Delta' },
      { symbol: 'ε', latex: '\\epsilon', name: 'Epsilon' },
      { symbol: 'θ', latex: '\\theta', name: 'Theta' },
      { symbol: 'λ', latex: '\\lambda', name: 'Lambda' },
      { symbol: 'μ', latex: '\\mu', name: 'Mu' },
      { symbol: 'π', latex: '\\pi', name: 'Pi' },
      { symbol: 'ρ', latex: '\\rho', name: 'Rho' },
      { symbol: 'σ', latex: '\\sigma', name: 'Sigma' },
      { symbol: 'τ', latex: '\\tau', name: 'Tau' },
      { symbol: 'φ', latex: '\\phi', name: 'Phi' },
      { symbol: 'ω', latex: '\\omega', name: 'Omega' },
      { symbol: 'Δ', latex: '\\Delta', name: 'Delta (cap)' },
      { symbol: 'Σ', latex: '\\Sigma', name: 'Sigma (cap)' },
      { symbol: 'Ω', latex: '\\Omega', name: 'Omega (cap)' },
      { symbol: 'Π', latex: '\\Pi', name: 'Pi (cap)' },
    ],
  },
  {
    name: 'Operations',
    symbols: [
      { symbol: '√', latex: '\\sqrt{x}', name: 'Square root', template: true },
      { symbol: '∛', latex: '\\sqrt[3]{x}', name: 'Cube root', template: true },
      { symbol: 'ⁿ√', latex: '\\sqrt[n]{x}', name: 'Nth root', template: true },
      { symbol: '∑', latex: '\\sum', name: 'Sum' },
      { symbol: '∏', latex: '\\prod', name: 'Product' },
      { symbol: '∫', latex: '\\int', name: 'Integral' },
      { symbol: '∂', latex: '\\partial', name: 'Partial' },
      { symbol: '∇', latex: '\\nabla', name: 'Nabla' },
      { symbol: 'lim', latex: '\\lim_{x \\to a}', name: 'Limit', template: true },
      { symbol: 'log', latex: '\\log', name: 'Log' },
      { symbol: 'ln', latex: '\\ln', name: 'Natural log' },
      { symbol: 'log₁₀', latex: '\\log_{10}', name: 'Log base 10' },
    ],
  },
  {
    name: 'Trig',
    symbols: [
      { symbol: 'sin', latex: '\\sin', name: 'Sine' },
      { symbol: 'cos', latex: '\\cos', name: 'Cosine' },
      { symbol: 'tan', latex: '\\tan', name: 'Tangent' },
      { symbol: 'cot', latex: '\\cot', name: 'Cotangent' },
      { symbol: 'sec', latex: '\\sec', name: 'Secant' },
      { symbol: 'csc', latex: '\\csc', name: 'Cosecant' },
      { symbol: 'sin⁻¹', latex: '\\sin^{-1}', name: 'Arcsine' },
      { symbol: 'cos⁻¹', latex: '\\cos^{-1}', name: 'Arccosine' },
      { symbol: 'tan⁻¹', latex: '\\tan^{-1}', name: 'Arctangent' },
      { symbol: 'sinh', latex: '\\sinh', name: 'Hyp sine' },
      { symbol: 'cosh', latex: '\\cosh', name: 'Hyp cosine' },
      { symbol: 'tanh', latex: '\\tanh', name: 'Hyp tangent' },
    ],
  },
  {
    name: 'Fractions',
    symbols: [
      { symbol: 'a/b', latex: '\\frac{a}{b}', name: 'Fraction', template: true },
      { symbol: 'x²', latex: 'x^{2}', name: 'Squared', template: true },
      { symbol: 'x³', latex: 'x^{3}', name: 'Cubed', template: true },
      { symbol: 'xⁿ', latex: 'x^{n}', name: 'Power', template: true },
      { symbol: 'x₁', latex: 'x_{1}', name: 'Subscript', template: true },
      { symbol: 'eˣ', latex: 'e^{x}', name: 'Exponential', template: true },
      { symbol: '|x|', latex: '|x|', name: 'Absolute', template: true },
      { symbol: 'ₐᵇ', latex: 'x_{a}^{b}', name: 'Sub+Super', template: true },
    ],
  },
  {
    name: 'Sets',
    symbols: [
      { symbol: '∈', latex: '\\in', name: 'Element of' },
      { symbol: '∉', latex: '\\notin', name: 'Not element' },
      { symbol: '⊂', latex: '\\subset', name: 'Subset' },
      { symbol: '⊃', latex: '\\supset', name: 'Superset' },
      { symbol: '⊆', latex: '\\subseteq', name: 'Subset eq' },
      { symbol: '∪', latex: '\\cup', name: 'Union' },
      { symbol: '∩', latex: '\\cap', name: 'Intersection' },
      { symbol: '∅', latex: '\\emptyset', name: 'Empty set' },
      { symbol: '∀', latex: '\\forall', name: 'For all' },
      { symbol: '∃', latex: '\\exists', name: 'Exists' },
      { symbol: '∴', latex: '\\therefore', name: 'Therefore' },
      { symbol: '∵', latex: '\\because', name: 'Because' },
      { symbol: 'ℝ', latex: '\\mathbb{R}', name: 'Real numbers' },
      { symbol: 'ℕ', latex: '\\mathbb{N}', name: 'Natural numbers' },
      { symbol: 'ℤ', latex: '\\mathbb{Z}', name: 'Integers' },
    ],
  },
  {
    name: 'Arrows',
    symbols: [
      { symbol: '→', latex: '\\rightarrow', name: 'Right arrow' },
      { symbol: '←', latex: '\\leftarrow', name: 'Left arrow' },
      { symbol: '↔', latex: '\\leftrightarrow', name: 'Both arrows' },
      { symbol: '⇒', latex: '\\Rightarrow', name: 'Implies' },
      { symbol: '⇐', latex: '\\Leftarrow', name: 'Implied by' },
      { symbol: '⇔', latex: '\\Leftrightarrow', name: 'Iff' },
      { symbol: '↑', latex: '\\uparrow', name: 'Up arrow' },
      { symbol: '↓', latex: '\\downarrow', name: 'Down arrow' },
      { symbol: '⟶', latex: '\\longrightarrow', name: 'Long right' },
    ],
  },
  {
    name: 'Geometry',
    symbols: [
      { symbol: '∠', latex: '\\angle', name: 'Angle' },
      { symbol: '⊥', latex: '\\perp', name: 'Perpendicular' },
      { symbol: '∥', latex: '\\parallel', name: 'Parallel' },
      { symbol: '≅', latex: '\\cong', name: 'Congruent' },
      { symbol: '∼', latex: '\\sim', name: 'Similar' },
      { symbol: '△', latex: '\\triangle', name: 'Triangle' },
      { symbol: '□', latex: '\\square', name: 'Square' },
      { symbol: '⊙', latex: '\\odot', name: 'Circle' },
      { symbol: '∘', latex: '\\circ', name: 'Composition' },
      { symbol: 'π', latex: '\\pi', name: 'Pi' },
    ],
  },
];

// Render LaTeX to HTML using KaTeX
const renderLatex = latex => {
  if (!latex || !latex.trim()) return null;
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: true,
      output: 'html',
    });
  } catch (e) {
    return `<span style="color: #ef4444;">Invalid LaTeX: ${e.message}</span>`;
  }
};

/**
 * EquationModal - A reusable modal for inserting LaTeX equations
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Called when modal is closed
 * @param {function} onInsert - Called with the LaTeX string when user clicks Insert
 * @param {string} initialValue - Initial value for the equation input
 */
export default function EquationModal({ isOpen, onClose, onInsert, initialValue = '' }) {
  const inputRef = useRef(null);
  const [equation, setEquation] = useState(initialValue);
  const [activeCategory, setActiveCategory] = useState('Common');
  const [previewHtml, setPreviewHtml] = useState('');

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setEquation(initialValue);
      setActiveCategory('Common');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, initialValue]);

  // Update preview when equation changes
  useEffect(() => {
    if (equation.trim()) {
      const html = renderLatex(equation);
      setPreviewHtml(html);
    } else {
      setPreviewHtml('');
    }
  }, [equation]);

  // Insert symbol/latex into equation input at cursor
  const insertSymbol = useCallback(
    latex => {
      if (!inputRef.current) return;

      const input = inputRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const text = equation;

      const newText = text.substring(0, start) + latex + text.substring(end);
      setEquation(newText);

      // Set cursor position after insert
      setTimeout(() => {
        const newPos = start + latex.length;
        input.focus();
        input.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [equation]
  );

  const handleInsert = () => {
    if (equation.trim()) {
      onInsert(equation.trim());
      setEquation('');
      onClose();
    }
  };

  const handleClose = () => {
    setEquation('');
    onClose();
  };

  const activeSymbols = SYMBOL_CATEGORIES.find(c => c.name === activeCategory)?.symbols || [];

  if (!isOpen) return null;

  return (
    <div className="equation-modal-overlay" onClick={handleClose}>
      <div className="equation-modal equation-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="equation-modal-header">
          <h3>Insert Equation</h3>
          <button type="button" className="equation-modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="equation-modal-body">
          {/* LaTeX Input */}
          <div className="equation-input-section">
            <label className="equation-input-label">LaTeX Expression</label>
            <input
              ref={inputRef}
              type="text"
              className="equation-input"
              value={equation}
              onChange={e => setEquation(e.target.value)}
              placeholder="Type LaTeX or click symbols below..."
              onKeyDown={e => {
                if (e.key === 'Enter' && equation.trim()) {
                  e.preventDefault();
                  handleInsert();
                }
                if (e.key === 'Escape') {
                  handleClose();
                }
              }}
            />
          </div>

          {/* Symbol Categories - Tabs */}
          <div className="symbol-categories">
            {SYMBOL_CATEGORIES.map(cat => (
              <button
                key={cat.name}
                type="button"
                className={`symbol-category-btn ${activeCategory === cat.name ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.name)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Symbol Grid */}
          <div className="symbol-grid">
            {activeSymbols.map((item, idx) => (
              <button
                key={idx}
                type="button"
                className={`symbol-btn ${item.template ? 'symbol-btn--template' : ''}`}
                title={`${item.name}${item.template ? ' (template)' : ''}`}
                onClick={() => insertSymbol(item.latex)}
              >
                {item.symbol}
              </button>
            ))}
          </div>

          {/* Live Preview */}
          <div className="equation-preview">
            <div className="equation-preview-label">Preview:</div>
            <div className="equation-preview-content">
              {previewHtml ? (
                <div
                  className="equation-preview-rendered"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <em className="equation-preview-placeholder">Click symbols or type LaTeX above</em>
              )}
            </div>
          </div>

          {/* Quick Reference */}
          <div className="equation-help">
            <strong>Tips:</strong> Use <code>^</code> for power (x^2), <code>_</code> for subscript
            (x_1), <code>\frac{'{a}{b}'}</code> for fractions. Templates show examples you can edit.
          </div>
        </div>

        <div className="equation-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleInsert}
            disabled={!equation.trim()}
          >
            Insert Equation
          </button>
        </div>
      </div>
    </div>
  );
}

// Export categories for external use if needed
export { SYMBOL_CATEGORIES };
